import Header from '../../components/feature/Header';
import Footer from '../../components/feature/Footer';
import TeamSection from './components/TeamSection';
import ValuesSection from './components/ValuesSection';
import { useSEO } from '../../utils/seo';

export default function About() {
  useSEO({
    title: 'À Propos de CoachFit - Notre Mission et Notre Équipe',
    description: 'Découvrez CoachFit, la première plateforme de coaching sportif en France. Plus de 5000 clients accompagnés depuis 2020 avec un taux de satisfaction de 98%. Rencontrez notre équipe de professionnels passionnés.',
    keywords: 'à propos CoachFit, mission coaching, équipe coaching, plateforme coaching sportif, histoire CoachFit',
    canonical: '/a-propos',
    ogImage: 'https://public.readdy.ai/ai/img_res/e56fab0c641e9800e3343bbdd9ec184b.jpg',
    schema: {
      "@context": "https://schema.org",
      "@type": "AboutPage",
      "name": "À Propos de CoachFit",
      "description": "Découvrez l'histoire, la mission et l'équipe de CoachFit",
      "url": `${import.meta.env.VITE_SITE_URL || ''}/a-propos`,
      "mainEntity": {
        "@type": "Organization",
        "name": "CoachFit",
        "foundingDate": "2020",
        "description": "Plateforme de coaching sportif connectant clients et coachs professionnels",
        "numberOfEmployees": "150+",
        "slogan": "Révélez votre potentiel avec les meilleurs coachs"
      }
    }
  });

  return (
    <div className="min-h-screen bg-white">
      <Header />
      <main>
        {/* Hero Section */}
        <section 
          className="relative py-20 bg-cover bg-center"
          style={{
            backgroundImage: `linear-gradient(rgba(59, 130, 246, 0.8), rgba(37, 99, 235, 0.8)), url('https://readdy.ai/api/search-image?query=Modern%20team%20of%20diverse%20professionals%20in%20bright%20office%20space%2C%20collaborative%20atmosphere%2C%20natural%20lighting%2C%20business%20meeting%20setting%2C%20inspiring%20workspace%20with%20plants%20and%20modern%20furniture&width=1200&height=600&seq=about-hero&orientation=landscape')`
          }}
        >
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center text-white">
            <h1 className="text-4xl md:text-5xl font-bold mb-6">
              À Propos de Notre Plateforme
            </h1>
            <p className="text-xl text-blue-100 max-w-3xl mx-auto">
              Nous connectons les personnes en quête de développement avec les meilleurs coachs professionnels pour créer des transformations durables
            </p>
          </div>
        </section>

        {/* Mission Section */}
        <section className="py-16 bg-white">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
              <div>
                <h2 className="text-3xl font-bold text-gray-900 mb-6">
                  Notre Mission
                </h2>
                <p className="text-lg text-gray-700 mb-6">
                  Nous croyons que chaque personne a un potentiel unique à révéler. Notre mission est de démocratiser l'accès au coaching professionnel en créant une plateforme qui connecte les individus avec des coachs certifiés et expérimentés.
                </p>
                <p className="text-lg text-gray-700 mb-6">
                  Depuis notre création en 2020, nous avons accompagné plus de 5 000 personnes dans leur développement personnel et professionnel, avec un taux de satisfaction de 98%.
                </p>
                <div className="grid grid-cols-2 gap-6">
                  <div className="text-center">
                    <div className="text-3xl font-bold text-blue-600 mb-2">5000+</div>
                    <p className="text-gray-600">Clients accompagnés</p>
                  </div>
                  <div className="text-center">
                    <div className="text-3xl font-bold text-blue-600 mb-2">150+</div>
                    <p className="text-gray-600">Coachs certifiés</p>
                  </div>
                </div>
              </div>
              <div>
                <img 
                  src="https://readdy.ai/api/search-image?query=Professional%20coaching%20session%20in%20modern%20office%2C%20coach%20and%20client%20having%20meaningful%20conversation%2C%20natural%20lighting%2C%20motivational%20atmosphere%2C%20business%20consultation%20setting%20with%20notebooks%20and%20coffee&width=600&height=400&seq=mission&orientation=landscape"
                  alt="Notre mission"
                  className="rounded-xl shadow-lg object-cover object-top w-full h-96"
                />
              </div>
            </div>
          </div>
        </section>

        {/* Values Section */}
        <ValuesSection />

        {/* Story Section */}
        <section className="py-16 bg-white">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
              <div>
                <img 
                  src="https://readdy.ai/api/search-image?query=Inspiring%20startup%20office%20space%20with%20diverse%20team%20working%20together%2C%20modern%20workspace%20with%20natural%20lighting%2C%20collaborative%20atmosphere%2C%20technology%20and%20innovation%20setting&width=600&height=400&seq=story&orientation=landscape"
                  alt="Notre histoire"
                  className="rounded-xl shadow-lg object-cover object-top w-full h-96"
                />
              </div>
              <div>
                <h2 className="text-3xl font-bold text-gray-900 mb-6">
                  Notre Histoire
                </h2>
                <p className="text-lg text-gray-700 mb-6">
                  Tout a commencé avec une vision simple : rendre le coaching accessible à tous. Nos fondateurs, eux-mêmes transformés par le coaching, ont voulu créer une plateforme qui facilite la rencontre entre coachs et clients.
                </p>
                <p className="text-lg text-gray-700 mb-6">
                  Aujourd'hui, nous sommes fiers d'être la première plateforme de coaching en France, avec une communauté grandissante de professionnels passionnés et de clients satisfaits.
                </p>
                <div className="space-y-4">
                  <div className="flex items-center">
                    <div className="w-4 h-4 bg-blue-600 rounded-full mr-4"></div>
                    <span className="text-gray-700">2020 - Création de la plateforme</span>
                  </div>
                  <div className="flex items-center">
                    <div className="w-4 h-4 bg-blue-600 rounded-full mr-4"></div>
                    <span className="text-gray-700">2021 - 50 premiers coachs certifiés</span>
                  </div>
                  <div className="flex items-center">
                    <div className="w-4 h-4 bg-blue-600 rounded-full mr-4"></div>
                    <span className="text-gray-700">2022 - 1000 clients accompagnés</span>
                  </div>
                  <div className="flex items-center">
                    <div className="w-4 h-4 bg-blue-600 rounded-full mr-4"></div>
                    <span className="text-gray-700">2024 - Leader du coaching en ligne</span>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* Team Section */}
        <TeamSection />

        {/* Certifications Section */}
        <section className="py-16 bg-gray-50">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="text-center mb-12">
              <h2 className="text-3xl font-bold text-gray-900 mb-4">
                Nos Certifications et Partenaires
              </h2>
              <p className="text-lg text-gray-600 max-w-2xl mx-auto">
                Nous travaillons avec les meilleures organisations pour garantir la qualité de nos services
              </p>
            </div>
            
            <div className="grid grid-cols-2 md:grid-cols-4 gap-8 items-center">
              <div className="text-center">
                <div className="w-20 h-20 bg-white rounded-full flex items-center justify-center mx-auto mb-4 shadow-md">
                  <i className="ri-award-line text-3xl text-blue-600"></i>
                </div>
                <h3 className="font-semibold text-gray-900">ICF Certifié</h3>
                <p className="text-sm text-gray-600">International Coach Federation</p>
              </div>
              
              <div className="text-center">
                <div className="w-20 h-20 bg-white rounded-full flex items-center justify-center mx-auto mb-4 shadow-md">
                  <i className="ri-shield-check-line text-3xl text-green-600"></i>
                </div>
                <h3 className="font-semibold text-gray-900">Qualité Garantie</h3>
                <p className="text-sm text-gray-600">Certification qualité ISO</p>
              </div>
              
              <div className="text-center">
                <div className="w-20 h-20 bg-white rounded-full flex items-center justify-center mx-auto mb-4 shadow-md">
                  <i className="ri-graduation-cap-line text-3xl text-purple-600"></i>
                </div>
                <h3 className="font-semibold text-gray-900">Formation Continue</h3>
                <p className="text-sm text-gray-600">Partenaire universités</p>
              </div>
              
              <div className="text-center">
                <div className="w-20 h-20 bg-white rounded-full flex items-center justify-center mx-auto mb-4 shadow-md">
                  <i className="ri-secure-payment-line text-3xl text-orange-600"></i>
                </div>
                <h3 className="font-semibold text-gray-900">Paiements Sécurisés</h3>
                <p className="text-sm text-gray-600">Transactions protégées</p>
              </div>
            </div>
          </div>
        </section>

        {/* CTA Section */}
        <section className="py-16 bg-blue-600">
          <div className="max-w-4xl mx-auto text-center px-4 sm:px-6 lg:px-8">
            <h2 className="text-3xl font-bold text-white mb-6">
              Rejoignez Notre Communauté
            </h2>
            <p className="text-xl text-blue-100 mb-8">
              Que vous soyez à la recherche d'un coach ou que vous souhaitiez devenir coach, nous sommes là pour vous accompagner
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <a href="/coachs" className="bg-white text-blue-600 px-8 py-3 rounded-lg font-semibold hover:bg-gray-100 transition-colors cursor-pointer whitespace-nowrap">
                Trouver un coach
              </a>
              <a href="/devenir-coach" className="border-2 border-white text-white px-8 py-3 rounded-lg font-semibold hover:bg-white hover:text-blue-600 transition-colors cursor-pointer whitespace-nowrap">
                Devenir coach
              </a>
            </div>
          </div>
        </section>
      </main>
      <Footer />
    </div>
  );
}
